import { MouseEventHandler, useState } from 'react';

import type { SxProps, Theme } from '@mui/material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Grid,
  Link,
  Paper,
  Typography
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { GoogleOAuthProvider } from '@react-oauth/google';

import GoogleLoginButton from '../GoogleLoginButton/GoogleLoginButton';
import FormLogin from '../FormLogin/FormLoginContainer';
import { GOOGLE_CLIENT_ID } from '../../utils/constants';

const styles: Record<string, SxProps<Theme>> = {
  root: {
    height: '100vh',
  },
  image: {
    backgroundImage: 'url("./background.png")',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  paper: {
    margin: theme => theme.spacing(8, 4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  paperContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  avatar: {
    margin: 1,
    backgroundColor: theme => theme.palette.secondary.main,
  },
  googleButtonContainer: {
    margin: theme => theme.spacing(6, 0, 3, 0),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  accordion: {
    mt: 8,
    display: 'flex',
    alignSelf: 'stretch',
    flexDirection: 'column'
  },
  accordionSummary: {
    alignItems: 'center',
  },
  accordionPrimaryText: {
    width: '60%',
    flexShrink: 0,
    fontWeight: 200
  },
  accordionSecondaryText: {
    color: 'text.secondary',
    fontWeight: 100,
    opacity: 0,
    '&:hover': {
      opacity: 'inherit',
    }
  },
  backdrop: {
    zIndex: (theme: Theme) => theme.zIndex.modal + 1,
     backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
};

function Copyright() {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {'Copyright © '}
      <Link color="inherit" href="https://intelliarts.com/">
        Intelliarts
      </Link>
      {` 1999-${new Date().getFullYear()}`}
    </Typography>
  );
}


type SignInPageProps = {
  onFailure: (error: unknown) => void,
  onUserLoggedIn: () => void,
  onForgotPassword: MouseEventHandler,
  isLoading?: boolean,
  onLoadingChange?: (isLoading: boolean) => void
}

export default function SignInPage(props: SignInPageProps) {
  const { onFailure, onUserLoggedIn, onForgotPassword, isLoading = false, onLoadingChange } = props;

  const [ expanded, setExpanded ] = useState(false);
  const toggleExpanded = () => setExpanded(prevValue => !prevValue);

  return (
    <Grid container component="main" sx={styles.root}>
      <Backdrop open={isLoading} sx={styles.backdrop}>
        <CircularProgress color="primary" />
      </Backdrop>
      <CssBaseline />
      <Grid size={{ xs: false, sm: 4, md: 7 }} sx={styles.image} />
      <Grid size={{ xs: 12, sm: 8, md: 5 }} component={Paper} sx={styles.paperContainer} elevation={6} square>
        <Box sx={styles.paper}>
          <Avatar sx={styles.avatar}><LockOutlinedIcon /></Avatar>

          <Typography component="h1" variant="h5">
            Sign in with
          </Typography>

          <Grid id="google-login-root-container" container sx={styles.googleButtonContainer}><Grid size="grow">

            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <GoogleLoginButton
                onUserLoggedIn={onUserLoggedIn}
                onFailure={onFailure}
                onLoadingChange={onLoadingChange} />
            </GoogleOAuthProvider>

          </Grid></Grid>

          <Accordion id="form-login-accordion"
                     sx={styles.accordion} expanded={expanded} onChange={toggleExpanded} elevation={0} square>
            <AccordionSummary id="form-login-accordion-summary" expandIcon={<ExpandMoreIcon />}
                              sx={styles.accordionSummary}>
              <Typography sx={styles.accordionPrimaryText}>No Google account? </Typography>
              <Typography sx={styles.accordionSecondaryText}>or mb U`r a Bot?</Typography>
            </AccordionSummary>

            <AccordionDetails id="form-login-accordion-details">
              <FormLogin
                onUserLoggedIn={onUserLoggedIn}
                onFailure={onFailure}
                onLoadingChange={onLoadingChange} />

              <Grid container><Grid size="grow">
                <Link id="forgot-password-link" href="#" variant="body2"
                      onClick={onForgotPassword}>
                  Forgot password?
                </Link>
              </Grid></Grid>
            </AccordionDetails>

          </Accordion>
        </Box>

        <Box mt={5}>
          <Copyright />
        </Box>
      </Grid>
    </Grid>
  );
}
